import ArticleCard from "@components/Blocks/Card/Article/ArticleCard";
import AdsContainer from "@components/Blocks/Container/AdsContainer";
import type { CreateLoadMoreHandlerProps } from "@components/Modules/LoadMore/CreateLoadMoreHandler";
import { CreateLoadMoreHandler } from "@components/Modules/LoadMore/CreateLoadMoreHandler";
import LoadMore from "@components/Modules/LoadMore/LoadMore";
import useSetFirstRenderedArticleIds from "@components/Modules/LoadMore/SetFirstRenderedArticleIds";
import { LOAD_MORE_CTA_COPY } from "@config/ctaCopy";
import { ENABLE_MYBH } from "@config/followTopic";
import { CompanionAdsLb } from "@elements/Advertisement/variant/CompanionAdsLb";
import FollowTopicsButton from "@elements/FollowTopicsButton/FollowTopicsButton";
import FollowTopicsToastMessage from "@elements/ToastMessage/FollowTopicsToastMessage";
import fetchBHApi from "@helper/fetchBHApi";
import { displayAfter } from "@helper/utils";
import useFollowTags from "@hooks/useFollowTags";
import useMySPHLightbox from "@hooks/useMySPHLightbox";
import type { ProcessedArticleData } from "@transformer/useOSResponse";
import {
  AdTargetingType,
  ArticleAdTargetKeys,
  PageAdTargetingTypeEnum,
} from "@typings/Ads.d";
import type { MoreArticlesRequestData } from "@typings/MoreArticlesApi";
import React, { useState } from "react";

const PAGE_SIZE = 10;
const INITIAL_ARTICLE_COUNT = 10;

type KeywordArticlesListProps = {
  articles: ProcessedArticleData[];
  tagAlias: string;
  tagName: string;
  tagId: string;
  articleCount: number;
  sectionName: string;
  adsSlotTarget?: AdTargetingType<ArticleAdTargetKeys, string>[];
};

export default function KeywordArticlesList({
  articles,
  tagAlias,
  tagName,
  tagId,
  articleCount,
  sectionName,
  adsSlotTarget,
}: KeywordArticlesListProps) {
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>(),
  );
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const { lightbox } = useMySPHLightbox();
  const { isFollowing, toggleFollow, loadingTopicId, showToast } =
    useFollowTags({
      onRequireLogin: () => lightbox?.initiateLogin(),
    });

  const showLoadMore: boolean = articles.length == INITIAL_ARTICLE_COUNT;

  /**
   * Sets a precaution for duplicate article ids when rendering additional articles
   */
  useSetFirstRenderedArticleIds(articles, setRenderedArticleIds);

  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length === 0 ? articles.at(-1) : loadMoreData.at(-1);
    const requestData: MoreArticlesRequestData = {
      filterArticlesBasedOn: tagAlias,
      sort: lastLoadedArticle?.sort,
    };

    const fetchMoreTagsStoriesData = async (
      requestData: MoreArticlesRequestData,
    ): Promise<ProcessedArticleData[] | []> => {
      const queryParams = new URLSearchParams({
        tagAlias: requestData.filterArticlesBasedOn as string,
        sort: Array.isArray(requestData.sort) ? requestData.sort.join(",") : "",
      }).toString();
      const response: ProcessedArticleData[] = await fetchBHApi(
        `more-tags-stories?${queryParams}`,
        "GET",
      );

      return response;
    };

    const createLoadMoreHandlerProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction: fetchMoreTagsStoriesData,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      lastLoadedArticle,
      loadMoreData,
      renderedArticleIds,
      pageSize: PAGE_SIZE,
      pageNumber,
    };

    const response = await CreateLoadMoreHandler(createLoadMoreHandlerProps);
    return response;
  };

  const renderTagsHeading = () => {
    if (ENABLE_MYBH) {
      return (
        <div className="flex pb-3 justify-between items-center border-b border-white-600">
          <div className="flex flex-col justify-center items-start font-primary leading-[150%]">
            <span className="text-black-800 text-md lg:text-[20px] font-semimedium">
              {tagName}
            </span>
            <span className="text-grey-400 text-xxs lg:text-xs font-regular">
              {articleCount} Artikel
            </span>
          </div>
          <div>
            {tagId && (
              <FollowTopicsButton
                label={isFollowing(tagId) ? "Diikuti" : "Ikut"}
                topicId={tagId}
                isFollowing={isFollowing(tagId)}
                isLoading={loadingTopicId === tagId}
                // Need to pass tagName here as label is "follow" in keyword page
                onFollow={(id) => toggleFollow(id, tagName)}
                onUnfollow={(id) => toggleFollow(id, tagName)}
              />
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white-200 my-8">
        <div className="p-4 flex items-center flex-col">
          <div className="font-regular italic text-black-200 text-[20px] lg:text-[30px] mb-2">
            {tagName}
          </div>
          <div className="font-regular italic text-grey-400 text-xxs lg:text-md">
            {articleCount} Artikel
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <FollowTopicsToastMessage forceShowToast={showToast} />
      <div className="w-12/12 lg:w-8/12">
        <div className="flex flex-col gap-8">
          {renderTagsHeading()}
          {[...articles, ...loadMoreData].map((context, index) => (
            <React.Fragment key={index}>
              {displayAfter(index, PAGE_SIZE) && (
                <AdsContainer key={index} className="bg-white-100">
                  <CompanionAdsLb
                    uniqueSectionName={sectionName}
                    index={index / PAGE_SIZE}
                    pageAdTargetType={PageAdTargetingTypeEnum.TAG}
                    articleSlotTarget={adsSlotTarget}
                  />
                </AdsContainer>
              )}
              <ArticleCard
                {...context}
                orientation="horizontal-summary"
                variant="2md"
                forNewDesign={true}
                displayWidth={284}
                displayHeight={190}
              />
            </React.Fragment>
          ))}
          {showLoadMore ? (
            <LoadMore
              rootClassName="my-4"
              onLoadMore={handleLoadMore}
              hasMore={hasMoreStories}
              loadText={LOAD_MORE_CTA_COPY}
              virtualPVTitle={tagName}
              pageNumber={pageNumber}
            />
          ) : null}
        </div>
      </div>
    </>
  );
}
